import React, { createRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReactCropperElement } from 'react-cropper';
import "cropperjs/dist/cropper.css";
import Webcam from 'react-webcam';
import { useUpload } from '../../hooks/useUpload';
import { useContactPhotoRequestMutationQuery } from '../../hooks/useContactPhotoRequestMutationQuery';
import { IContactPhoto } from '../../types/profilephoto';
import { BlockUI, Loader, } from '../../components/Loader';
import { PhotoUpload } from './PhotoUpload';
import { PhotoIDUpload } from './PhotoIDUpload';
import BypassConfirmModal from './BypassConfirmModal';
import { useContactPhotoRequest } from '../../hooks/useContactPhotoRequest';
import { getContactId, getDate, getExtByMimeType } from '../../tools/utils';
import { AccessControl, useGlobalContext } from '../../hooks/Helper/GlobalContext';
import { request } from '../../services/request';
import { useTranslation } from 'react-i18next';
import '../../sass/base/PhotoManagement.scss';
import logo from '../../assets/images/Selfie - Acceptable.png';
import maskImage from '../../assets/images/Mask.png';
import selfieHatSunglasses from '../../assets/images/Selfie - hat-glasses.png';
import selfieBlurry from '../../assets/images/Selfie - blurry.png';
import selfiePoorlyFramed from '../../assets/images/Selfie - poorly framed.png';
import selfieBusyBackground from '../../assets/images/Selfie - Busy Background.png';
import idAcceptable from '../../assets/images/ID - acceptable.png';
import idBlurry from '../../assets/images/ID - Blurry.png';
import idBlackedOut from '../../assets/images/ID - blacked out info or expired.png';
import idImproperiyCropped from '../../assets/images/ID - improperly cropped.png';
import idNotOfficial from '../../assets/images/ID - not official.png';
import lockIcon from '../../assets/images/lock-icon.png';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { getUser } from '../../redux/slices/contactSlice';
import { t } from 'i18next';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ── MediaPipe Selfie Segmentation ─────────────────────────────────────────────
// Singleton model — loaded once, reused for every submission.
let _segInstance: any = null;
let _segReady = false;


const initSegmentation = async (): Promise<void> => {
    if (_segReady) return;
    try {
        const { SelfieSegmentation } = await import('@mediapipe/selfie_segmentation');
        _segInstance = new SelfieSegmentation({
            locateFile: (file: string) =>
                `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
        });
        _segInstance.setOptions({ modelSelection: 0 });
        await _segInstance.initialize();
        _segReady = true;
    } catch {
        _segInstance = null;
    }
};

// Uses the segmentation mask to isolate true background pixels, then checks their
// luminance variance. Ignores hair, clothing, and accessories entirely.
const checkBackgroundWithSegmentation = (blob: Blob): Promise<boolean> =>
    new Promise(resolve => {
        if (!_segInstance || !_segReady) { resolve(false); return; }
        const img = new Image();
        const url = URL.createObjectURL(blob);
        img.onload = async () => {
            URL.revokeObjectURL(url);
            try {
                _segInstance.onResults((results: any) => {
                    const w = img.naturalWidth, h = img.naturalHeight;

                    const mc = document.createElement('canvas');
                    mc.width = w; mc.height = h;
                    const mCtx = mc.getContext('2d')!;
                    mCtx.drawImage(results.segmentationMask, 0, 0, w, h);
                    const maskD = mCtx.getImageData(0, 0, w, h).data;

                    const ic = document.createElement('canvas');
                    ic.width = w; ic.height = h;
                    const iCtx = ic.getContext('2d')!;
                    iCtx.drawImage(img, 0, 0);
                    const imgD = iCtx.getImageData(0, 0, w, h).data;

                    // mask near black (< 128) = background pixel
                    const lums: number[] = [];
                    const step = 3;
                    for (let i = 0; i < maskD.length; i += 4 * step) {
                        if (maskD[i] < 128) {
                            lums.push(0.299 * imgD[i] + 0.587 * imgD[i + 1] + 0.114 * imgD[i + 2]);
                        }
                    }

                    if (lums.length < 200) { resolve(false); return; }
                    const mean = lums.reduce((s, v) => s + v, 0) / lums.length;
                    const variance = lums.reduce((s, v) => s + (v - mean) ** 2, 0) / lums.length;
                    resolve(variance > 800);
                });
                await _segInstance.send({ image: img });
            } catch { resolve(false); }
        };
        img.src = url;
    });

const BLUR_THRESHOLD = 0.0004;   // unchanged tuned value — the FP-safe hard gate
const BLUR_BORDERLINE = 0.0006;  // upper edge of the "mildly blurry" band (tune with real samples)

const measureSharpness = (blob: Blob): Promise<boolean> =>
    new Promise(resolve => {
       if (/Android/i.test(navigator.userAgent)) { resolve(false); return; }

        const img = new Image();
        const url = URL.createObjectURL(blob);
        img.onload = () => {
            try {
                const size = 200;
                const patchScore = (cx: number, cy: number): number => {
                    const sx = Math.max(0, Math.min(img.width - size, Math.round(cx - size / 2)));
                    const sy = Math.max(0, Math.min(img.height - size, Math.round(cy - size / 2)));
                    const c = document.createElement('canvas');
                    c.width = c.height = size;
                    const ctx = c.getContext('2d')!;
                    ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
                    const d = ctx.getImageData(0, 0, size, size).data;
                    const lum = (p: number) => (0.299 * d[p] + 0.587 * d[p + 1] + 0.114 * d[p + 2]) / 255;
                    let score = 0;
                    for (let i = size + 1; i < size * (size - 1) - 1; i++) {
                        const p = i * 4;
                        const lap = -lum(p - size * 4) - lum(p - 4) + 4 * lum(p) - lum(p + 4) - lum(p + size * 4);
                        score += lap * lap;
                    }
                    return score / (size * size);
                };

                // Primary check — the original centre patch and tuned threshold, unchanged.
                const cx = img.width / 2, cy = img.height / 2;
                const center = patchScore(cx, cy);
                (window as any).__lastBlurScore = center.toFixed(6);

                if (center < BLUR_THRESHOLD) { resolve(true); return; }    // clearly blurry
                if (center >= BLUR_BORDERLINE) { resolve(false); return; } // clearly sharp — untouched, no FP

                // Borderline band only: look for any clearly-sharp region nearby. If even the
                // crispest neighbouring patch is still soft, it's genuine mild blur.
                const dx = img.width / 6, dy = img.height / 6;
                const offsets: [number, number][] = [[-dx, 0], [dx, 0], [0, -dy], [0, dy]];
                for (const [ox, oy] of offsets) {
                    if (patchScore(cx + ox, cy + oy) >= BLUR_BORDERLINE) { resolve(false); return; }
                }
                resolve(true); // no sharp region anywhere → mildly blurry
            } catch { resolve(false); }
            finally { URL.revokeObjectURL(url); }
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(false); };
        img.src = url;
    });

// Validates a selfie blob against our backend API.
// Returns all error strings found, or an empty array if the photo passes every check.
// Returns raw error codes — translation happens at render time so language switching works instantly.
const validateSelfieBlob = async (blob: Blob, height: number, width: number): Promise<string[]> => {
    try {
        const [jsonResponse, isBusyBackground, isBlurry] = await Promise.all([
            request({
                endpoint: 'contactphotorequest/validate-selfie',
                method: 'POST',
                data: blob,
                type: 'data',
                headers: {
                    'X-Image-Height': height.toString(),
                    'X-Image-Width': width.toString(),
                    ...(/Android/i.test(navigator.userAgent) && { 'X-Platform': 'android' }),
                }
            }),
            initSegmentation().then(() => checkBackgroundWithSegmentation(blob)),
            measureSharpness(blob),
        ]);

        if (!jsonResponse.success) return ['__unableToVerify'];

        const data = jsonResponse.data?.Data;
        if (!data || data.length === 0) return ['NoFaceDetected'];

        const codes: string[] = data[0]?.errorCodes || [];
        const faceFound = !codes.includes('NoFaceDetected');
        if (isBusyBackground && !codes.includes('BackgroundTooBusy'))
            codes.push('BackgroundTooBusy');
        if (faceFound && isBlurry && !codes.includes('ImageBlurry'))
            codes.push('ImageBlurry');

        return codes;
    } catch (err) {
        console.error('Selfie validation failed:', err);
        return ['__verificationError'];
    }
};

// Returns raw error codes — translation happens at render time so language switching works instantly.
const validatePhotoIDBlob = async (blob: Blob): Promise<string[]> => {
    try {
        const jsonResponse = await request({
            endpoint: 'contactphotorequest/validate-photo-id',
            method: 'POST',
            data: blob,
            type: 'data',
        });

        if (!jsonResponse.success) return ['__unableToVerify'];

        const data = jsonResponse.data?.Data;
        if (!data || data.length === 0) return ['NoFaceDetected'];

        return data[0]?.errorCodes || [];
    } catch (err) {
        console.error('Photo ID validation failed:', err);
        return ['__verificationError'];
    }
};

// ─── myCCO theme ──────────────────────────────────────────────────────────────
const THEME = '#c67b05';
const THEME_SHADOW = 'rgba(198,123,5,0.32)';
const THEME_COMPLETE = '#c05c1e'; // orange for Step 7


// ─── Shared small helper components ──────────────────────────────────────────
const DoGrid = ({ items }: { items: { icon: string; label: string }[] }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {items.map((item, i) => (
            <div key={i} style={{ background: '#f0fdf4', borderRadius: 12, padding: '14px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 28 }}>{item.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', textAlign: 'center', lineHeight: 1.3 }}>{item.label}</span>
            </div>
        ))}
    </div>
);

const DontGrid = ({ items }: { items: { label: string; img: string; tip: string }[] }) => (
    <div className="sg-dont-grid">
        {items.map((item, i) => (
            <div key={i} className="sg-dont-grid__item" style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                <div className="sg-dont-grid__img-wrap">
                    <img src={item.img} alt={item.label} className="sg-dont-grid__img" />
                    <div className="sg-dont-grid__x">✕</div>
                    <div className="sg-dont-grid__overlay">{item.tip}</div>
                </div>
                <span className="sg-dont-grid__label">{item.label}</span>
            </div>
        ))}
    </div>
);


// ─── Badge helpers ──────────────────────────────────────────────────────────
const SectionBadge = ({ type }: { type: 'do' | 'dont' }) => {
    const { t } = useTranslation();
    return (
        <div className="sg-badge">
            <div className={`sg-badge__icon sg-badge__icon--${type}`}>
                <span>{type === 'do' ? '✓' : '✕'}</span>
            </div>
            <span className="sg-badge__label">
                {type === 'do' ? t('PhotoManagement.badge.do') : t('PhotoManagement.badge.dont')}
            </span>
        </div>
    );
};

// ─── Corner viewfinder brackets ──────────────────────────────────────────────
const ViewfinderCorners = ({ bottom = 36 }: { bottom?: number }) => (
    <>
        <div style={{ position: 'absolute', top: 14, left: 14, width: 22, height: 22, borderTop: '2px solid rgba(255,255,255,0.7)', borderLeft: '2px solid rgba(255,255,255,0.7)', zIndex: 10, borderRadius: '2px 0 0 0' }} />
        <div style={{ position: 'absolute', top: 14, right: 14, width: 22, height: 22, borderTop: '2px solid rgba(255,255,255,0.7)', borderRight: '2px solid rgba(255,255,255,0.7)', zIndex: 10, borderRadius: '0 2px 0 0' }} />
        <div style={{ position: 'absolute', bottom: bottom, left: 14, width: 22, height: 22, borderBottom: '2px solid rgba(255,255,255,0.7)', borderLeft: '2px solid rgba(255,255,255,0.7)', zIndex: 10, borderRadius: '0 0 0 2px' }} />
        <div style={{ position: 'absolute', bottom: bottom, right: 14, width: 22, height: 22, borderBottom: '2px solid rgba(255,255,255,0.7)', borderRight: '2px solid rgba(255,255,255,0.7)', zIndex: 10, borderRadius: '0 0 2px 0' }} />
    </>
);

// ─── Main component ───────────────────────────────────────────────────────────
export const PhotoManagement = ({ headerMarginTop }: { headerMarginTop: number }) => {

    const { t } = useTranslation();
    const contactId = getContactId();
    const photoQuery = useContactPhotoRequest({ contactId: contactId });
    const photoData = photoQuery.data?.data?.Data;
    const { contact, loading, error } = useSelector((state: RootState) => state.contact);
    const isProfileIncomplete = contact !== null && !error && contact.IsProfileCompleted === false
        && photoQuery.isFetched && (!photoData || photoData.length <= 0);

    // ─── Core state (UNCHANGED) ─────────────────────────────────────────────
    const [showLoading, setLoading] = useState(false);
    const [showFaceError, setFaceError] = useState(false);
    const [isPhotoCroped, setisPhotoCroped] = useState(false);
    const [faceError, setError] = useState('');
    const [isPhotoIDCroped, setisPhotoIDCroped] = useState(false);
    const [image, setImage] = useState('');
    const [imagePhotoID, setImagePhotoID] = useState('');
    const [PhotoBlob, setPhotoBlob] = useState<null | Blob>(null);
    const [PhotoHeight, setPhotoHeight] = useState<number>(0);
    const [PhotoWidth, setPhotoWidth] = useState<number>(0);
    const [PhotoIDBlob, setPhotoIDBlob] = useState<null | Blob>(null);
    const cropperPhotoRef = useRef<ReactCropperElement>(null);
    const cropperPhotoIdRef = useRef<ReactCropperElement>(null);
    const { CandidateAccessControl, CandidateId, FullName } = useGlobalContext();
    const lastname = FullName ? FullName.split(' ').pop() : '-';

    // ─── Mobile state ───────────────────────────────────────────────────────
    const [capturedSelfie, setCapturedSelfie] = useState(false);
    const [capturedID, setCapturedID] = useState(false);
    const [idOrientation, setIdOrientation] = useState<'landscape' | 'portrait'>('landscape');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [retakingFrom, setRetakingFrom] = useState<'selfie' | 'id' | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 767);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const [currentStep, setCurrentStep] = useState(1);
    const [selfieZoom, setSelfieZoom] = useState(1);
    const [idZoom, setIdZoom] = useState(1);
    const [isIDCapturing, setIsIDCapturing] = useState(false);



    // ─── Step-3 early selfie validation ─────────────────────────────────────
    const [selfieValidating, setSelfieValidating] = useState(false);
    const [selfieError, setSelfieError] = useState<string[]>([]);

    // Step-5 Photo ID face validation
    const [photoIDValidating, setPhotoIDValidating] = useState(false);
    const [photoIDError, setPhotoIDError] = useState<string[]>([]);

    // Bypass confirmation state
    const [showSelfieBypassModal, setShowSelfieBypassModal] = useState(false);
    const [selfieBypassReason, setSelfieBypassReason] = useState('');
    const [selfieBypassConfirmed, setSelfieBypassConfirmed] = useState(false);
    const [showPhotoIDBypassModal, setShowPhotoIDBypassModal] = useState(false);
    const [photoIDBypassReason, setPhotoIDBypassReason] = useState('');
    const [photoIDBypassConfirmed, setPhotoIDBypassConfirmed] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    useEffect(() => {
        if (contact === null && !loading && !error) {
            dispatch(getUser());
        }
    }, []);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 767);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Pre-warm segmentation model on mount — covers both webcam and file upload paths
    // useEffect(() => { initSegmentation(); }, []); // DISABLED — re-enable when needed

    // When switching from mobile to desktop, keep the step
    useEffect(() => {
        // No longer clamping currentStep to 3 for desktop
    }, [isMobile]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [currentStep]);



    // Auto-process uploaded file on mobile step 3 (skip cropper — convert directly to blob)
    useEffect(() => {
        if (isMobile && image && currentStep === 3) {
            fetch(image).then(r => r.blob()).then(blob => {
                const img = new Image();
                img.src = image;
                img.onload = () => {
                    setPhotoHeight(img.naturalHeight);
                    setPhotoWidth(img.naturalWidth);
                    setPhotoBlob(blob);
                    setisPhotoCroped(true);
                    setImage('');
                    setCapturedSelfie(true);
                };
            });
        }
    }, [image]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isMobile && imagePhotoID && currentStep === 5) {
            fetch(imagePhotoID).then(r => r.blob()).then(blob => {
                setPhotoIDBlob(blob);
                setisPhotoIDCroped(true);
                setImagePhotoID('');
                setCapturedID(true);
            });
        }
    }, [imagePhotoID]); // eslint-disable-line react-hooks/exhaustive-deps

    // Run face API + background check as soon as a selfie blob is ready (step 3).
    // Sends the blob directly as octet-stream — no upload to storage needed.
    useEffect(() => {
        if (!PhotoBlob || !isPhotoCroped) {
            setSelfieError([]);
            setSelfieValidating(false);
            return;
        }
        setSelfieValidating(true);
        setSelfieError([]);
        validateSelfieBlob(PhotoBlob, PhotoHeight, PhotoWidth)
            .then(errs => setSelfieError(errs))
            .catch(() => setSelfieError(['__verificationError']))
            .finally(() => setSelfieValidating(false));
    }, [PhotoBlob, PhotoHeight, PhotoWidth]); // eslint-disable-line react-hooks/exhaustive-deps

    // Run Photo ID face detection as soon as an ID blob is ready (step 5).
    useEffect(() => {
        if (!PhotoIDBlob || !isPhotoIDCroped) {
            setPhotoIDError([]);
            setPhotoIDValidating(false);
            return;
        }
        setPhotoIDValidating(true);
        setPhotoIDError([]);
        validatePhotoIDBlob(PhotoIDBlob)
            .then(errs => setPhotoIDError(errs))
            .catch(() => setPhotoIDError(['__verificationError']))
            .finally(() => setPhotoIDValidating(false));
    }, [PhotoIDBlob]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── onChange handlers (UNCHANGED) ─────────────────────────────────────
    const onChange = (e: any) => {
        e.preventDefault();
        let files;
        if (e.dataTransfer) { files = e.dataTransfer.files; }
        else if (e.target) { files = e.target.files; }
        const file = files[0];
        const allowedImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (allowedImageTypes.includes(file.type) && fileExtension !== 'jfif') {
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result as any);
                setisPhotoCroped(false);
                setPhotoBlob(null);
                setCam(false);
                e.target.value = ''; // Allow re-selecting same file
            };
            reader.readAsDataURL(file);
        } else {
            alert(t('PhotoManagement.PhotoManagement.error'));
        }
    };

    const onChangePhotoID = (e: any) => {
        e.preventDefault();
        let files;
        if (e.dataTransfer) { files = e.dataTransfer.files; }
        else if (e.target) { files = e.target.files; }
        const file = files[0];
        const allowedImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (allowedImageTypes.includes(file.type) && fileExtension !== 'jfif') {
            const reader = new FileReader();
            reader.onload = () => {
                setImagePhotoID(reader.result as any);
                setisPhotoIDCroped(false);
                setPhotoIDBlob(null);
                setCamPhotoID(false);
                e.target.value = ''; // Allow re-selecting same file
            };
            reader.readAsDataURL(file);
        } else {
            alert(t('PhotoManagement.PhotoManagement.error'));
        }
    };

    // ─── Crop handlers (UNCHANGED) ──────────────────────────────────────────
    const getCropData = () => {
        if (typeof cropperPhotoRef.current?.cropper !== "undefined") {
            const canvas = cropperPhotoRef.current?.cropper.getCroppedCanvas();
            const dataURL = canvas.toDataURL('image/jpeg', 0.9);
            fetch(dataURL).then(res => res.blob()).then(async (blob) => {
                setImage('');
                setPhotoHeight(canvas.height);
                setPhotoWidth(canvas.width);
                setPhotoBlob(blob);
                setisPhotoCroped(true);
                setCam(false);
            }).catch(error => console.error("Error converting data URL to Blob:", error));
        }
    };

    const getCropDataPhotoID = () => {
        if (typeof cropperPhotoIdRef.current?.cropper !== "undefined") {
            const dataURL = cropperPhotoIdRef.current?.cropper.getCroppedCanvas().toDataURL('image/jpeg', 0.9);
            fetch(dataURL).then(res => res.blob()).then(blob => {
                setImagePhotoID('');
                setPhotoIDBlob(blob);
                setisPhotoIDCroped(true);
                setCamPhotoID(false);
            }).catch(error => console.error("Error converting data URL to Blob:", error));
        }
    };

    // ─── Reset handlers ─────────────────────────────────────────────────────
    const resetImage = () => {
        setImage('');
        setPhotoBlob(null);
        setPhotoHeight(0);
        setPhotoWidth(0);
        setisPhotoCroped(false);
        setFaceError(false);
        setSelfieBypassConfirmed(false);
        setSelfieBypassReason('');
        setSelfieError([]);
        if (isMobile) {
            setCapturedSelfie(false);
            setCam(true); // immediately re-open camera on mobile retake
        } else {
            setCapturedSelfie(false);
            setCam(false);
        }
    };

    const resetImagePhotoID = () => {
        setImagePhotoID('');
        setPhotoIDBlob(null);
        setisPhotoIDCroped(false);
        // setIdBlurWarning(false); // DISABLED — re-enable when needed
        setCapturedID(false);
        setPhotoIDBypassConfirmed(false);
        setPhotoIDBypassReason('');
        setPhotoIDError([]);
        if (isMobile) {
            setCamPhotoID(true); // immediately re-open camera on mobile retake
        } else {
            setCamPhotoID(false);
        }
    };

    // ─── Zoom / Rotate (UNCHANGED) ──────────────────────────────────────────
    const handleZoomIn = () => { cropperPhotoRef?.current?.cropper?.zoom(0.1); };
    const handleZoomOut = () => { cropperPhotoRef?.current?.cropper?.zoom(-0.1); };
    const handleZoomInPhotoID = () => { cropperPhotoIdRef?.current?.cropper?.zoom(0.1); };
    const handleZoomOutPhotoID = () => { cropperPhotoIdRef?.current?.cropper?.zoom(-0.1); };
    const handlerotateImage = () => { if (cropperPhotoRef?.current?.cropper) cropperPhotoRef.current.cropper.rotate(90); };
    const handleRotatePhotoID = () => { if (cropperPhotoIdRef?.current?.cropper) cropperPhotoIdRef.current.cropper.rotate(90); };

    // ─── Webcam (UNCHANGED refs) ────────────────────────────────────────────
    const webcamRef = useRef<Webcam>(null);
    const webcamPhotoIDRef = useRef<Webcam>(null);
    const [useCam, setCam] = useState(false);
    const [useCamPhotoID, setCamPhotoID] = useState(false);

    // Toggle body classes and browser fullscreen when cameras open/close.
    // Fullscreen is only exited when NEITHER camera is active to avoid one
    // effect cancelling the other camera's fullscreen session.
    useEffect(() => {
        const anyCameraActive = isMobile && (useCam || useCamPhotoID);

        if (isMobile && useCam) {
            document.body.classList.add('pm-camera-selfie-active');
            const el = document.documentElement;
            if (el.requestFullscreen) el.requestFullscreen().catch(() => { });
        } else {
            document.body.classList.remove('pm-camera-selfie-active');
        }

        if (isMobile && useCamPhotoID) {
            document.body.classList.add('pm-camera-id-active');
            const el = document.documentElement;
            if (el.requestFullscreen) el.requestFullscreen().catch(() => { });
        } else {
            document.body.classList.remove('pm-camera-id-active');
        }

        if (!anyCameraActive && document.fullscreenElement && document.exitFullscreen) {
            document.exitFullscreen().catch(() => { });
        }

        return () => {
            document.body.classList.remove('pm-camera-selfie-active');
            document.body.classList.remove('pm-camera-id-active');
            if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => { });
        };
    }, [isMobile, useCam, useCamPhotoID]);


    // Desktop capture (sets image → triggers cropper)
    const captureImageFromWebcam = () => {
        const imageSrc = webcamRef?.current?.getScreenshot();
        setImage(imageSrc as string);
        setCam(false);
    };

    const captureImageFromWebcamPhotoID = () => {
        const imageSrc = webcamPhotoIDRef?.current?.getScreenshot();
        setImagePhotoID(imageSrc as string);
        setCamPhotoID(false);
    };

    const mobileCaptureFromWebcam = () => {
        const video = (webcamRef.current as any)?.video as HTMLVideoElement | null;
        if (!video || video.readyState < 2 || video.videoWidth === 0) return;

        const nw = video.videoWidth;
        const nh = video.videoHeight;

        // Capture EXACTLY the region shown by object-fit: cover in the live preview,
        // so the saved photo is pixel-for-pixel what the user framed (true WYSIWYG).
        // The selfie container is locked to 5:7, so this region is already 5:7 — no
        // extra crop is applied, which avoids any rounding drift between preview and capture.
        const container = video.closest('.pm-cam-container') as HTMLElement | null;

        let srcX: number, srcY: number, srcW: number, srcH: number;

        if (container) {
            const cW = container.clientWidth;
            const cH = container.clientHeight;
            const coverScale = Math.max(cW / nw, cH / nh);
            srcW = Math.round(cW / coverScale);
            srcH = Math.round(cH / coverScale);
            srcX = Math.round((nw - srcW) / 2);
            srcY = Math.round((nh - srcH) / 2);
        } else {
            // Fallback: 5:7 center crop of raw video
            if (nw / nh > 5 / 7) {
                srcH = nh; srcW = Math.round(nh * 5 / 7);
                srcX = Math.round((nw - srcW) / 2); srcY = 0;
            } else {
                srcW = nw; srcH = Math.round(nw * 7 / 5);
                srcX = 0; srcY = Math.round((nh - srcH) / 2);
            }
        }

        const outW = srcW;
        const outH = srcH;

        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            // Front camera is displayed mirrored (CSS scaleX(-1)). drawImage reads raw pixels,
            // so replicate the flip here to match what the user saw on screen.
            ctx.translate(outW, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
        }

        canvas.toBlob((blob) => {
            if (blob) {
                setPhotoHeight(canvas.height);
                setPhotoWidth(canvas.width);
                setPhotoBlob(blob);
                setisPhotoCroped(true);
                setImage('');
                setCam(false);
                setCapturedSelfie(true);

            }
        }, 'image/jpeg', 0.95);
    };

    const mobileCaptureIDFromWebcam = () => {
        if (isIDCapturing) return;
        setError('');

        const isLandscape = idOrientation === 'landscape';
        const video = (webcamPhotoIDRef.current as any)?.video as HTMLVideoElement | null;

        if (!video || video.readyState < 2 || video.videoWidth === 0) return;

        const nw = video.videoWidth;
        const nh = video.videoHeight;

        // Get the actual rendered container size so the SVG→video mapping is exact
        const containerEl = video.closest('.pm-cam-container') as HTMLElement | null;
        const cW = containerEl?.clientWidth || nw;
        const cH = containerEl?.clientHeight || nh;

        // SVG viewBox and dashed-boundary rect coords (must match the overlay SVG)
        const vbW = isLandscape ? 133 : 100;
        const vbH = isLandscape ? 100 : 133;
        const rX = isLandscape ? 13 : 12;
        const rY = isLandscape ? 15 : 13;
        const rW = isLandscape ? 107 : 76;
        const rH = isLandscape ? 70 : 107;

        // SVG uses preserveAspectRatio="xMidYMid meet" → fit inside container, centered
        const svgScale = Math.min(cW / vbW, cH / vbH);
        const svgOffX = (cW - vbW * svgScale) / 2;
        const svgOffY = (cH - vbH * svgScale) / 2;

        // Dashed rect in container-pixel coords
        const rectL_c = svgOffX + rX * svgScale;
        const rectT_c = svgOffY + rY * svgScale;
        const rectW_c = rW * svgScale;
        const rectH_c = rH * svgScale;

        // Video uses object-fit: cover → scale to fill container, centered (may overflow)
        const videoScale = Math.max(cW / nw, cH / nh);
        const videoOffX = (cW - nw * videoScale) / 2; // negative = video extends beyond left edge
        const videoOffY = (cH - nh * videoScale) / 2;

        // Map container coords → raw video pixel coords
        const cropX = (rectL_c - videoOffX) / videoScale;
        const cropY = (rectT_c - videoOffY) / videoScale;
        const cropW = rectW_c / videoScale;
        const cropH = rectH_c / videoScale;

        setIsIDCapturing(true);

        requestAnimationFrame(() => {
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(cropW);
            canvas.height = Math.round(cropH);

            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
            }

            canvas.toBlob((blob) => {
                if (blob) {
                    setPhotoIDBlob(blob);
                    setisPhotoIDCroped(true);
                    setCamPhotoID(false);
                    setIdZoom(1);
                    setCapturedID(true);
                }
                setIsIDCapturing(false);
            }, 'image/jpeg', 0.95);
        });
    };

    // Desktop ID capture — same SVG→pixel math as mobileCaptureIDFromWebcam but sets
    // PhotoIDBlob directly instead of routing through the mobile crop confirmation flow.
    const desktopCaptureIDFromWebcam = () => {
        if (isIDCapturing) return;
        setError('');

        const isLand = idOrientation === 'landscape';
        const video = (webcamPhotoIDRef.current as any)?.video as HTMLVideoElement | null;
        if (!video || video.readyState < 2 || video.videoWidth === 0) return;

        const nw = video.videoWidth;
        const nh = video.videoHeight;

        const containerEl = video.closest('.pm-cam-container') as HTMLElement | null;
        const cW = containerEl?.clientWidth || nw;
        const cH = containerEl?.clientHeight || nh;

        const vbW = isLand ? 133 : 100;
        const vbH = isLand ? 100 : 133;
        const rX = isLand ? 13 : 12;
        const rY = isLand ? 15 : 13;
        const rW = isLand ? 107 : 76;
        const rH = isLand ? 70 : 107;

        const svgScale = Math.min(cW / vbW, cH / vbH);
        const svgOffX = (cW - vbW * svgScale) / 2;
        const svgOffY = (cH - vbH * svgScale) / 2;

        const rectL_c = svgOffX + rX * svgScale;
        const rectT_c = svgOffY + rY * svgScale;
        const rectW_c = rW * svgScale;
        const rectH_c = rH * svgScale;

        const videoScale = Math.max(cW / nw, cH / nh);
        const videoOffX = (cW - nw * videoScale) / 2;
        const videoOffY = (cH - nh * videoScale) / 2;

        const cropX = (rectL_c - videoOffX) / videoScale;
        const cropY = (rectT_c - videoOffY) / videoScale;
        const cropW = rectW_c / videoScale;
        const cropH = rectH_c / videoScale;

        setIsIDCapturing(true);
        requestAnimationFrame(() => {
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(cropW);
            canvas.height = Math.round(cropH);
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
            }
            canvas.toBlob((blob) => {
                if (blob) {
                    setPhotoIDBlob(blob);
                    setisPhotoIDCroped(true);
                    setCamPhotoID(false);
                    setIdZoom(1);
                    setCapturedID(true);
                }
                setIsIDCapturing(false);
            }, 'image/jpeg', 0.95);
        });
    };


    const startSelfieCamera = () => { setPhotoBlob(null); setisPhotoCroped(false); setImage(''); setCam(true); };
    const startSelfieCameraPhotoID = () => { setPhotoIDBlob(null); setisPhotoIDCroped(false); setImagePhotoID(''); setCamPhotoID(true); };

    // ─── Submit handler ─────────────────────────────────────────────────────
    const PhotoMutation = useContactPhotoRequestMutationQuery();
    const uploadFile = useUpload();

    const submitHandler = async () => {
        setLoading(true);
        setFaceError(false);
        setError('');
        const upload: any[] = [];
        const uploadedFiles: any[] = [];
        const submissionDate = getDate('MMDDYY');
        const data: IContactPhoto = {
            ContactId: contactId,
            Name: `${lastname}-${CandidateId}-${submissionDate}`,
            PhotoIdLicenceURL: PhotoIDBlob,
            PhotoURL: PhotoBlob,
            ...(selfieBypassConfirmed && {
                SelfieBypass: true,
                SelfieErrors: selfieError.map(resolveValidationError).join('; '),
                SelfieBypassReason: selfieBypassReason,
            }),
            ...(photoIDBypassConfirmed && {
                PhotoIDBypass: true,
                PhotoIDErrors: photoIDError.map(resolveValidationError).join('; '),
                PhotoIDBypassReason: photoIDBypassReason,
            }),
        };

        if (data.PhotoURL instanceof Blob) {
            const extension = getExtByMimeType(data.PhotoURL.type);
            upload.push({ name: 'PhotoURL', file: new File([data.PhotoURL], `PhotoURL.${extension}`, { type: data.PhotoURL.type }) });
        }
        if (data.PhotoIdLicenceURL instanceof Blob) {
            const extension = getExtByMimeType(data.PhotoIdLicenceURL.type);
            upload.push({ name: 'PhotoIdLicenceURL', file: new File([data.PhotoIdLicenceURL], `PhotoIdLicenceURL.${extension}`, { type: data.PhotoIdLicenceURL.type }) });
        }

        if (upload.length) {
            upload.forEach(file => {
                const formData = new FormData();
                formData.append('file', file.file);
                uploadedFiles.push(uploadFile.mutateAsync({
                    body: formData,
                    type: file.file.type.includes('image') ? 'image' : 'file',
                    isPrivate: file.name === 'PhotoIdLicenceURL' ? true : false,
                }));
            });
        }

        Promise.all(uploadedFiles)
            .then(async files => {
                if (2 === files.length) {
                    if (data.PhotoURL) data.PhotoURL = files[0].data.Data.FileName;
                    if (data.PhotoIdLicenceURL) data.PhotoIdLicenceURL = files[1].data.Data.FileName;
                }


                // Selfie quality already validated at step 3 via validateSelfieBlob.
                PhotoMutation.mutateAsync({ body: data, type: 'POST' }).then(res => {
                    if (res?.data?.ApiCode === 200 && res.success === true) {
                        setIsSubmitted(true);
                        setCurrentStep(7);
                    }
                }).finally(() => setLoading(false));
            })
            .catch(() => setLoading(false));
    };

    // ─── Shared helpers ─────────────────────────────────────────────────────
    const MOBILE_STEP_LABELS = [
        t('PhotoManagement.steps.overview'),
        t('PhotoManagement.steps.selfieTips'),
        t('PhotoManagement.steps.takeSelfie'),
        t('PhotoManagement.steps.idTips'),
        t('PhotoManagement.steps.takeIdPhoto'),
        t('PhotoManagement.steps.reviewSubmit'),
        t('PhotoManagement.steps.complete'),
    ];
    const mobileStepLabel = MOBILE_STEP_LABELS[currentStep - 1] || '';
    const progressColor = isSubmitted ? THEME_COMPLETE : THEME;

    const resolveValidationError = (code: string): string => {
        if (code === '__unableToVerify') return t('PhotoManagement.errors.unableToVerify');
        if (code === '__verificationError') return t('PhotoManagement.errors.verificationError');
        return t(`PhotoManagement.validationErrors.${code}`);
    };

    const handleSelfieReviewContinue = () => {
        if (selfieError.length > 0 && !selfieBypassConfirmed) {
            setShowSelfieBypassModal(true);
            return;
        }
        if (retakingFrom === 'selfie') { setRetakingFrom(null); setCurrentStep(6); }
        else setCurrentStep(4);
    };

    const handleIdReviewContinue = () => {
        if (photoIDError.length > 0 && !photoIDBypassConfirmed) {
            setShowPhotoIDBypassModal(true);
            return;
        }
        setRetakingFrom(null);
        setCurrentStep(6);
    };

    const handleSelfieBypassCancel = () => {
        setShowSelfieBypassModal(false);
        resetImage();
        startSelfieCamera();
        setCurrentStep(3);
    };

    const handlePhotoIDBypassCancel = () => {
        setShowPhotoIDBypassModal(false);
        resetImagePhotoID();
        startSelfieCameraPhotoID();
        setCurrentStep(5);
    };

    const renderNavBar = (opts: {
        onBack?: () => void;
        onContinue: () => void;
        continueLabel: string;
        continueDisabled?: boolean;
        backLabel?: string;
    }) => (
        <div className="pm-nav-bar">
            {opts.onBack && (
                <button onClick={opts.onBack} className="pm-btn-secondary">
                    {opts.backLabel || t('PhotoManagement.nav.back')}
                </button>
            )}
            <button
                onClick={opts.continueDisabled ? undefined : opts.onContinue}
                disabled={!!opts.continueDisabled}
                className="pm-btn-primary pm-btn-primary--full"
            >
                {opts.continueLabel}
            </button>
        </div>
    );

    // ─── Desktop: sticky header (6-segment bar) ──────────────────────────────

    const renderDesktopHeader = () => (
        <div className="pm-desktop-header">
            <div className="pm-desktop-header__top">
                <div className="pm-desktop-header__icon-wrap">
                    <img src={lockIcon} alt="lock" />
                </div>
                <div className="pm-desktop-header__info">
                    <div className="pm-desktop-header__title">{t('PhotoManagement.PhotoManagement.IdentityVerification')}</div>
                    <div className="pm-desktop-header__subtitle">{t('PhotoManagement.header.secureTagline')}</div>
                </div>
                <div className="pm-desktop-header__step-badge">
                    {t('PhotoManagement.header.stepOf', { step: currentStep })}
                </div>
            </div>

            <div className="pm-desktop-header__progress">
                {[1, 2, 3, 4, 5, 6].map(s => (
                    <div
                        key={s}
                        className="pm-desktop-header__progress-segment"
                        style={{
                            background: s <= currentStep ? progressColor : '#edf0f5',
                            boxShadow: s === currentStep ? `0 0 8px ${THEME_SHADOW}` : 'none'
                        }}
                    />
                ))}
            </div>
            <div className="pm-desktop-header__label">
                <span>{mobileStepLabel}</span>
            </div>
        </div>
    );

    // ─── Desktop: step content ───────────────────────────────────────────────
    const renderDesktopStep = () => {

        // ── STEP 1: Overview 
        if (currentStep === 1) return (
            <div className="pm-overview">
                <h1 className="pm-overview__title">
                    {t('PhotoManagement.PhotoManagement.OverviewTitle')}
                </h1>
                <p className="pm-overview__desc">
                    {t('PhotoManagement.PhotoManagement.OverviewDesc')}
                </p>

                <div className="pm-overview__items">
                    {[
                        {
                            icon: '🤳',
                            titleKey: 'PhotoManagement.PhotoManagement.SelfiePhotoTitle',
                            subKey: 'PhotoManagement.PhotoManagement.SelfiePhotoSub',
                        },
                        {
                            icon: (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={THEME} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="5" width="20" height="14" rx="2" /><circle cx="8" cy="12" r="2" /><path d="M14 9h4M14 12h4M14 15h2" />
                                </svg>
                            ),
                            titleKey: 'PhotoManagement.PhotoManagement.PhotoIDTitle',
                            subKey: 'PhotoManagement.PhotoManagement.PhotoIDSub',
                        },
                    ].map((item, i) => (
                        <div key={i} className="pm-overview__item">
                            <div className="pm-overview__item-icon">{item.icon}</div>
                            <div>
                                <div className="pm-overview__item-title">{t(item.titleKey)}</div>
                                <div className="pm-overview__item-sub">{t(item.subKey)}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="pm-overview__notice">
                    <span className="pm-overview__notice-icon"><img src={lockIcon} alt="lock" style={{ width: 20, height: 20, objectFit: 'contain', verticalAlign: 'middle' }} /></span>
                    <p className="pm-overview__notice-text">
                        {t('PhotoManagement.PhotoManagement.PrivacyNote')}{' '}
                        <strong className="pm-overview__notice-deleted">
                            {t('PhotoManagement.PhotoManagement.PermanentlyDeleted')}
                        </strong>{' '}
                        {t('PhotoManagement.PhotoManagement.OnceVerified')}
                    </p>
                </div>

                <button
                    onClick={() => setCurrentStep(2)}
                    className="pm-overview__btn"
                    style={{ background: THEME, boxShadow: `0 4px 16px ${THEME_SHADOW}` }}
                >
                    {t('PhotoManagement.PhotoManagement.ContinueBtn')}
                </button>
            </div>
        );

        // ── STEP 2: Selfie Guidelines ─────────────────────────────────────────
        if (currentStep === 2) return (
            <div className="sg">
                <div className="sg__header">
                    <h2 className="sg__title">{t('PhotoManagement.selfieGuidelines.title')}</h2>
                    <p className="sg__subtitle">{t('PhotoManagement.selfieGuidelines.subtitle')}</p>
                </div>

                <div className="sg__row">
                    {/* Acceptable image + DO bullets */}
                    <div className="sg__acceptable-card">
                        <div className="sg__acceptable-body">
                            <div className="sg__photo-col">
                                <div className="sg__acceptable-label">{t('PhotoManagement.selfieGuidelines.acceptable')}</div>
                                <div className="sg__photo-wrap">
                                    <img src={logo} alt="Acceptable selfie" className="sg__photo" />
                                    <div className="sg__check-badge">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    </div>
                                </div>
                            </div>
                            <div className="sg__do-section">
                                <div className="sg__do-header">
                                    <div className="sg__do-badge"><span>✓</span></div>
                                    <span className="sg__do-title">{t('PhotoManagement.selfieGuidelines.doLabel')}</span>
                                </div>
                                {[
                                    { icon: '😊', label: t('PhotoManagement.selfieGuidelines.do.faceVisible'), tooltip: t('PhotoManagement.selfieGuidelines.do.faceVisibleTip') },
                                    { icon: '💡', label: t('PhotoManagement.selfieGuidelines.do.goodLighting'), tooltip: t('PhotoManagement.selfieGuidelines.do.goodLightingTip') },
                                    { icon: '👤', label: t('PhotoManagement.selfieGuidelines.do.headShoulders'), tooltip: t('PhotoManagement.selfieGuidelines.do.headShouldersTip') },
                                    { icon: '⬜', label: t('PhotoManagement.selfieGuidelines.do.plainBackground'), tooltip: t('PhotoManagement.selfieGuidelines.do.plainBackgroundTip') },
                                ].map((item, i) => (
                                    <div key={i} className="sg__do-item" data-tooltip={item.tooltip} style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                                        <span className="sg__do-item-icon">{item.icon}</span>
                                        <span className="sg__do-item-label">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* DON'T card */}
                    <div className="sg__dont-card">
                        <SectionBadge type="dont" />
                        <DontGrid items={[
                            { label: t('PhotoManagement.selfieGuidelines.dont.notCentered'), img: selfiePoorlyFramed, tip: t('PhotoManagement.selfieGuidelines.dont.notCenteredTip') },
                            { label: t('PhotoManagement.selfieGuidelines.dont.noHat'), img: selfieHatSunglasses, tip: t('PhotoManagement.selfieGuidelines.dont.noHatTip') },
                            { label: t('PhotoManagement.selfieGuidelines.dont.notBlurry'), img: selfieBlurry, tip: t('PhotoManagement.selfieGuidelines.dont.notBlurryTip') },
                            { label: t('PhotoManagement.selfieGuidelines.dont.noBusyBackground'), img: selfieBusyBackground, tip: t('PhotoManagement.selfieGuidelines.dont.noBusyBackgroundTip') },
                        ]} />
                    </div>
                </div>

                <div className="sg__nav">
                    <button onClick={() => setCurrentStep(1)} className="pm-btn-secondary">{t('PhotoManagement.nav.back')}</button>
                    <button onClick={() => { startSelfieCamera(); setCurrentStep(3); }} className="pm-btn-primary pm-btn-primary--lg">{t('PhotoManagement.nav.continueToCamera')}</button>
                </div>
            </div>
        );

        // ── STEP 3: Take Selfie (Desktop) ─────────────────────────────────────
        if (currentStep === 3) {
            // 3A: Post-capture review — matches mobile UI exactly
            if (capturedSelfie) return (
                <div className="pm-desktop-step-container">
                    {showSelfieBypassModal && (
                        <BypassConfirmModal
                            errors={selfieError}
                            resolveError={resolveValidationError}
                            onConfirm={(reason) => {
                                setSelfieBypassReason(reason);
                                setSelfieBypassConfirmed(true);
                                setShowSelfieBypassModal(false);
                                if (retakingFrom === 'selfie') { setRetakingFrom(null); setCurrentStep(6); }
                                else setCurrentStep(4);
                            }}
                            onCancel={handleSelfieBypassCancel}
                        />
                    )}
                    <h2 className="pm-selfie-review__title">{t('PhotoManagement.selfieCamera.title')}</h2>
                    <div className="pm-preview-box pm-selfie-review__preview">
                        {PhotoBlob && <img src={URL.createObjectURL(PhotoBlob)} alt="Captured selfie" className="pm-preview-img" />}
                        <span className={`pm-preview-badge${selfieError.length > 0 && !selfieValidating ? ' pm-preview-badge--error' : ''}`}>
                            {selfieError.length > 0 && !selfieValidating
                                ? t('PhotoManagement.selfieCamera.capturedWithErrors')
                                : t('PhotoManagement.selfieCamera.captured')}
                        </span>
                        {selfieValidating && (
                            <div className="pm-selfie-review__loader-overlay">
                                <div className="pm-selfie-review__loader-spinner">
                                    <Loader scale={0.55} />
                                </div>
                            </div>
                        )}
                    </div>

                    {selfieError.length > 0 && !selfieValidating && (
                        <div className="cco_form_message cco_form_message_error pm-selfie-review__error">
                            <span className="pm-beta-tag">BETA</span>
                            <ul>
                                {selfieError.map((code, i) => (
                                    <li key={i}>
                                        {resolveValidationError(code)}
                                    </li>
                                ))}
                            </ul>
                            <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                        </div>
                    )}

                    <button onClick={() => { resetImage(); startSelfieCamera(); }} className="pm-btn-secondary--bronze pm-selfie-review__retake">
                        {t('PhotoManagement.selfieCamera.retakeSelfie')}
                    </button>

                    <div className="pm-selfie-review__nav">
                        <button onClick={() => { resetImage(); startSelfieCamera(); }} className="pm-btn-secondary">{t('PhotoManagement.nav.back')}</button>
                        <button onClick={handleSelfieReviewContinue} disabled={selfieValidating} className="pm-btn-primary pm-btn-primary--lg">
                            {t('PhotoManagement.nav.continue')}
                        </button>
                    </div>
                </div>
            );

            // 3: Camera modal — opens immediately (startSelfieCamera called on navigate here)
            return (
                <div className="pm-desktop-step-container">
                    <div className="pm-selfie-review__header">
                        <h2>{t('PhotoManagement.selfieCamera.title')}</h2>
                        <p>{t('PhotoManagement.selfieCamera.subtitle')}</p>
                    </div>
                    <PhotoUpload
                        PhotoBlob={PhotoBlob}
                        captureImageFromWebcam={mobileCaptureFromWebcam}
                        cropperPhotoRef={cropperPhotoRef}
                        getCropData={getCropData}
                        handleZoomIn={handleZoomIn}
                        handleZoomOut={handleZoomOut}
                        image={image}
                        isMobile={false}
                        isPhotoCroped={isPhotoCroped}
                        onChange={onChange}
                        resetImage={() => { resetImage(); setCurrentStep(2); }}
                        useCam={useCam}
                        startSelfieCamera={startSelfieCamera}
                        webcamRef={webcamRef}
                        showFaceError={showFaceError}
                        key={1}
                        rotateImage={handlerotateImage}
                        faceError={faceError}
                    />
                </div>
            );
        }

        // ── STEP 4: Photo ID Guidelines ───────────────────────────────────────
        if (currentStep === 4) return (
            <div className="sg pm-desktop-step-container">
                <div className="sg__header">
                    <h2 className="sg__title">{t('PhotoManagement.idGuidelines.title')}</h2>
                    <p className="sg__subtitle">{t('PhotoManagement.idGuidelines.subtitle')}</p>
                </div>

                {/* Priority 3A: physical card warning */}


                <div className="sg__row">
                    {/* Acceptable card: ID image + DO checklist */}
                    <div className="sg__acceptable-card">
                        <div className="sg__acceptable-label">{t('PhotoManagement.idGuidelines.acceptable')}</div>
                        <div className="sg__acceptable-body">
                            <div className="sg__photo-col">
                                <div className="sg__photo-wrap">
                                    <img
                                        src={idAcceptable}
                                        alt="Acceptable ID"
                                        className="sg__photo"
                                        style={{ width: 200, height: 'auto', borderRadius: 10 }}
                                    />
                                    <div className="sg__check-badge">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    </div>
                                </div>
                                <p style={{ margin: '18px 0 0', fontSize: 11, fontWeight: 700, color: '#1d4ed8', textAlign: 'center' }}>{t('PhotoManagement.idGuidelines.entireCardVisible')}</p>
                            </div>
                            <div className="sg__do-section">
                                <div className="sg__do-header">
                                    <div className="sg__do-badge"><span>✓</span></div>
                                    <span className="sg__do-title">{t('PhotoManagement.idGuidelines.doLabel')}</span>
                                </div>
                                {[
                                    { icon: '🪪', label: t('PhotoManagement.idGuidelines.do.fullIdVisible'), tooltip: t('PhotoManagement.idGuidelines.do.fullIdVisibleTip') },
                                    { icon: '📅', label: t('PhotoManagement.idGuidelines.do.expiryDateClear'), tooltip: t('PhotoManagement.idGuidelines.do.expiryDateClearTip') },
                                    { icon: '🎂', label: t('PhotoManagement.idGuidelines.do.dateOfBirthVisible'), tooltip: t('PhotoManagement.idGuidelines.do.dateOfBirthVisibleTip') },
                                    { icon: '💡', label: t('PhotoManagement.idGuidelines.do.wellLit'), tooltip: t('PhotoManagement.idGuidelines.do.wellLitTip') },
                                ].map((item, i) => (
                                    <div key={i} className="sg__do-item" data-tooltip={item.tooltip} style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                                        <span className="sg__do-item-icon">{item.icon}</span>
                                        <span className="sg__do-item-label">{item.label}</span>
                                    </div>
                                ))}
                                <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 12px', marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start', border: '1px solid #bfdbfe' }}>
                                    <span style={{ fontSize: 14, flexShrink: 0 }}>📋</span>
                                    <span style={{ fontSize: 12, color: '#334155', lineHeight: 1.5 }}>
                                        <strong>{t('PhotoManagement.idGuidelines.requiredLabel')}</strong> {t('PhotoManagement.idGuidelines.requiredFields')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* DON'T card */}
                    <div className="sg__dont-card">
                        <SectionBadge type="dont" />
                        <DontGrid items={[
                            { label: t('PhotoManagement.idGuidelines.dont.notCropped'), img: idImproperiyCropped, tip: t('PhotoManagement.idGuidelines.dont.notCroppedTip') },
                            { label: t('PhotoManagement.idGuidelines.dont.notBlurry'), img: idBlurry, tip: t('PhotoManagement.idGuidelines.dont.notBlurryTip') },
                            { label: t('PhotoManagement.idGuidelines.dont.dontCoverText'), img: idBlackedOut, tip: t('PhotoManagement.idGuidelines.dont.dontCoverTextTip') },
                            { label: t('PhotoManagement.idGuidelines.dont.mustBeOfficial'), img: idNotOfficial, tip: t('PhotoManagement.idGuidelines.dont.mustBeOfficialTip') },
                        ]} />
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 15 }}>
                    <button onClick={() => setCurrentStep(3)} className="pm-btn-secondary">{t('PhotoManagement.nav.backToSelfie')}</button>
                    <button onClick={() => { startSelfieCameraPhotoID(); setCurrentStep(5); }} className="pm-btn-primary pm-btn-primary--lg">{t('PhotoManagement.nav.continueToCamera')}</button>
                </div>
            </div>
        );

        // ── STEP 5: Take ID Photo (Desktop) ───────────────────────────────────
        if (currentStep === 5) {
            // 5A: Post-capture review
            if (capturedID) return (
                <div className="pm-desktop-step-container">
                    {showPhotoIDBypassModal && (
                        <BypassConfirmModal
                            errors={photoIDError}
                            resolveError={resolveValidationError}
                            onConfirm={(reason) => {
                                setPhotoIDBypassReason(reason);
                                setPhotoIDBypassConfirmed(true);
                                setShowPhotoIDBypassModal(false);
                                setRetakingFrom(null);
                                setCurrentStep(6);
                            }}
                            onCancel={handlePhotoIDBypassCancel}
                        />
                    )}
                    <h2 className="pm-selfie-review__title">{t('PhotoManagement.idCamera.title')}</h2>
                    <p className="pm-step-note">
                        {t('PhotoManagement.PhotoIDUpload.Note')} <b style={{ color: '#d9534f' }}>{t('PhotoManagement.PhotoIDUpload.permanentlydeleted')}</b> {t('PhotoManagement.PhotoIDUpload.Verified')}
                    </p>
                    <div className="pm-preview-box pm-selfie-review__preview">
                        {PhotoIDBlob && <img src={URL.createObjectURL(PhotoIDBlob)} alt="Captured ID" className="pm-preview-img" />}
                        <span className={`pm-preview-badge${photoIDError.length > 0 && !photoIDValidating ? ' pm-preview-badge--error' : ''}`}>
                            {photoIDError.length > 0 && !photoIDValidating
                                ? t('PhotoManagement.idCamera.capturedWithErrors')
                                : t('PhotoManagement.selfieCamera.captured')}
                        </span>
                        {photoIDValidating && (
                            <div className="pm-selfie-review__loader-overlay">
                                <div className="pm-selfie-review__loader-spinner">
                                    <Loader scale={0.55} />
                                </div>
                            </div>
                        )}
                    </div>

                    {photoIDError.length > 0 && !photoIDValidating && (
                        <div className="cco_form_message cco_form_message_error pm-selfie-review__error">
                            <ul>
                                {photoIDError.map((code, i) => (
                                    <li key={i}>
                                        <span className="pm-beta-tag">BETA</span>
                                        {resolveValidationError(code)}
                                    </li>
                                ))}
                            </ul>
                            <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                        </div>
                    )}

                    <button onClick={() => { resetImagePhotoID(); startSelfieCameraPhotoID(); }} className="pm-btn-secondary--bronze pm-selfie-review__retake">
                        {t('PhotoManagement.idCamera.retakePhoto')}
                    </button>

                    <div className="pm-selfie-review__nav">
                        <button onClick={() => { resetImagePhotoID(); startSelfieCameraPhotoID(); }} className="pm-btn-secondary">{t('PhotoManagement.nav.back')}</button>
                        <button onClick={handleIdReviewContinue} disabled={photoIDValidating} className="pm-btn-primary pm-btn-primary--lg">
                            {t('PhotoManagement.nav.review')}
                        </button>
                    </div>
                </div>
            );

            // 5: Camera modal — opens immediately (startSelfieCameraPhotoID called on navigate here)
            return (
                <div className="pm-desktop-step-container">
                    <div className="pm-selfie-review__header">
                        <h2>{t('PhotoManagement.idCamera.photographTitle')}</h2>
                        <p>{t('PhotoManagement.idCamera.photographSubtitle')}</p>
                    </div>
                    <PhotoIDUpload
                        captureImageFromWebcamPhotoID={captureImageFromWebcamPhotoID}
                        cropperPhotoIdRef={cropperPhotoIdRef}
                        getCropDataPhotoID={getCropDataPhotoID}
                        handleZoomInPhotoID={handleZoomInPhotoID}
                        handleZoomOutPhotoID={handleZoomOutPhotoID}
                        imagePhotoID={imagePhotoID}
                        isMobile={false}
                        isPhotoIDCroped={isPhotoIDCroped}
                        onChangePhotoID={onChangePhotoID}
                        resetImagePhotoID={() => { resetImagePhotoID(); setCurrentStep(4); }}
                        useCamPhotoID={useCamPhotoID}
                        startSelfieCameraPhotoID={startSelfieCameraPhotoID}
                        webcamPhotoIDRef={webcamPhotoIDRef}
                        key={2}
                        rotateImageID={handleRotatePhotoID}
                        idOrientation={idOrientation}
                        onOrientationChange={setIdOrientation}
                        idZoom={idZoom}
                        onZoomChange={setIdZoom}
                        onCapture={desktopCaptureIDFromWebcam}
                        isIDCapturing={isIDCapturing}
                    />
                </div>
            );
        }

        // ── STEP 6: Review & Submit (Desktop: Side-by-Side) ────────────────────
        if (currentStep === 6) return (
            <div className="pm-desktop-step-container">
                <div className="pm-review-header">
                    <h2>{t('PhotoManagement.review.title')}</h2>
                    <p>{t('PhotoManagement.review.subtitle')}</p>
                </div>

                <div className="pm-review-grid">
                    {/* Selfie Preview */}
                    <div className="pm-review-card">
                        <div className="pm-review-card__header">
                            <span className="pm-review-card__title">{t('PhotoManagement.review.selfieLabel')}</span>
                            {selfieError.length > 0
                                ? <span className="pm-review-card__badge--error">{t('PhotoManagement.selfieCamera.capturedWithErrors')}</span>
                                : <span className="pm-review-card__badge">{t('PhotoManagement.review.ready')}</span>
                            }
                        </div>
                        <div className="pm-review-card__photo">
                            {PhotoBlob && <img src={URL.createObjectURL(PhotoBlob)} alt="Selfie" />}
                        </div>
                        {selfieError.length > 0 && (
                            <div className="cco_form_message cco_form_message_error pm-selfie-review__error">
                                <span className="pm-beta-tag">BETA</span>
                                <ul>
                                    {selfieError.map((code, i) => (
                                        <li key={i}>{resolveValidationError(code)}</li>
                                    ))}
                                </ul>
                                <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                            </div>
                        )}
                        <div className={`pm-review-card__footer${selfieError.length > 0 ? ' pm-review-card__footer--flush' : ''}`}>
                            <button onClick={() => { resetImage(); setRetakingFrom('selfie'); startSelfieCamera(); setCurrentStep(3); }} className="pm-btn-ghost">{t('PhotoManagement.review.retakeSelfie')}</button>
                        </div>
                    </div>

                    {/* ID Preview */}
                    <div className="pm-review-card pm-review-card--id">
                        <div className="pm-review-card__header">
                            <span className="pm-review-card__title">{t('PhotoManagement.review.idLabel')}</span>
                            {photoIDError.length > 0
                                ? <span className="pm-review-card__badge--error">{t('PhotoManagement.idCamera.capturedWithErrors')}</span>
                                : <span className="pm-review-card__badge">{t('PhotoManagement.review.ready')}</span>
                            }
                        </div>
                        <div className="pm-review-card__photo">
                            {PhotoIDBlob && <img src={URL.createObjectURL(PhotoIDBlob)} alt="ID" />}
                        </div>
                        {photoIDError.length > 0 && (
                            <div className="cco_form_message cco_form_message_error pm-selfie-review__error">
                                <span className="pm-beta-tag">BETA</span>
                                <ul>
                                    {photoIDError.map((code, i) => (
                                        <li key={i}>{resolveValidationError(code)}</li>
                                    ))}
                                </ul>
                                <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                            </div>
                        )}
                        <div className={`pm-review-card__footer${photoIDError.length > 0 ? ' pm-review-card__footer--flush' : ''}`}>
                            <button onClick={() => { resetImagePhotoID(); setRetakingFrom('id'); startSelfieCameraPhotoID(); setCurrentStep(5); }} className="pm-btn-ghost">{t('PhotoManagement.review.retakePhoto')}</button>
                        </div>
                    </div>
                </div>

                {showFaceError && (
                    <div className="cco_form_message cco_form_message_error pm-review-error">
                        {faceError ? faceError : t('PhotoManagement.PhotoUpload.PhotoRec')}
                        <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                    </div>
                )}

                <div className="pm-review-submit-wrap">
                    <button onClick={submitHandler} disabled={showLoading} className="pm-btn-primary pm-btn-primary--lg">
                        {showLoading ? <Loader /> : t('PhotoManagement.review.submitBtn')}
                    </button>
                </div>
            </div>
        );

        // ── STEP 7: Success (Desktop) ─────────────────────────────────────────
        if (currentStep === 7 || isSubmitted) return (
            <div className="pm-success pm-desktop-step-container">
                <div className="pm-success__icon-wrap">
                    <div className="pm-success__icon-glow">
                        <div className="pm-success__icon-box">
                            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                    </div>
                </div>
                <h2 className="pm-success__title pm-success__title--desktop">{t('PhotoManagement.success.title')}</h2>
                <p className="pm-success__desc pm-success__desc--desktop">
                    {t('PhotoManagement.success.desc')}
                </p>
                <div className="pm-success__tags">
                    {[t('PhotoManagement.success.selfieTag'), t('PhotoManagement.success.idPhotoTag')].map(tag => (
                        <div key={tag} className="pm-success__tag">{tag}</div>
                    ))}
                </div>
                <a href="/dashboard" className="pm-btn-complete">
                    {t('PhotoManagement.success.returnBtn')}
                </a>
            </div>
        );

        return null;
    };

    // ─── MAIN RENDER ──────────────────────────────────────────────────────────


    // ─── Mobile: selfie camera view ─────────────────────────────────────────
    const renderSelfieCamera = () => {
        const isFullScreen = isMobile && useCam;
        const cameraContent = (
            <>
                <div className="pm-cam-container pm-cam-container--selfie">
                    {useCam ? (
                        <Webcam
                            audio={false}
                            ref={webcamRef}
                            screenshotFormat='image/jpeg'
                            screenshotQuality={1}
                            videoConstraints={{ facingMode: 'user', width: { ideal: 1440 }, height: { ideal: 1080 } }}
                            mirrored={true}
                            className="pm-webcam-preview"
                        />
                    ) : (
                        <div className="pm-cam-placeholder pm-cam-placeholder--portrait" />
                    )}

                    <img
                        src={maskImage}
                        alt=""
                        className="pm-cam-mask-overlay"
                    />

                    <div className="pm-cam-hud">
                        <div className="pm-cam-hud-title"></div>
                    </div>
                </div>

                <div className="pm-cam-bottom-bar">
                    <button className="pm-cam-back-btn" onClick={() => { setCam(false); setCurrentStep(2); }}>←</button>
                    <div className="pm-cam-capture-wrap">
                        <span className="pm-cam-capture-label">{t('PhotoManagement.selfieCamera.cameraLabel')}</span>
                        <button
                            className="pm-cam-shutter"
                            onTouchEnd={(e) => { e.preventDefault(); (useCam ? mobileCaptureFromWebcam : startSelfieCamera)(); }}
                            onClick={useCam ? mobileCaptureFromWebcam : startSelfieCamera}
                            style={{ touchAction: 'manipulation' }}
                        >
                            <div className={`pm-cam-shutter-inner${useCam ? '' : ' pm-cam-shutter-inner--start'}`} />
                        </button>
                    </div>
                    <div className="pm-cam-spacer" />
                </div>
            </>
        );

        if (isFullScreen) {
            // Portal to <body> so position:fixed always anchors to the viewport,
            // regardless of any transformed/overflow ancestor in the page tree
            // (which would otherwise turn the overlay into a scrollable box).
            return createPortal(<div className="pm-fullscreen-camera">{cameraContent}</div>, document.body);
        }
        return cameraContent;
    };

    // ─── Mobile: ID camera view ──────────────────────────────────────────────
    const renderIDCamera = () => {
        const isLandscape = idOrientation === 'landscape';
        const isFullScreen = isMobile && useCamPhotoID;

        const cameraContent = (
            <>
                {/* Orientation toggle */}
                <div className="pm-ori-toggle-wrap">
                    <div className="pm-ori-toggle">
                        {(['landscape', 'portrait'] as const).map(ori => (
                            <button
                                key={ori}
                                onClick={() => setIdOrientation(ori)}
                                className="pm-ori-btn"
                                data-active={idOrientation === ori}
                            >
                                {ori === 'landscape' ? t('PhotoManagement.idCamera.landscape') : t('PhotoManagement.idCamera.portrait')}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Camera view */}
                <div className="pm-cam-container" style={{ position: 'relative', width: '100%', borderRadius: 12, overflow: 'visible', background: '#000', aspectRatio: isLandscape ? '4/3' : '3/4', maxHeight: 500, maxWidth: isLandscape ? undefined : 375, margin: '0 auto' }}>
                    <ViewfinderCorners bottom={34} />
                    {useCamPhotoID ? (
                        <>
                            <Webcam
                                audio={false}
                                ref={webcamPhotoIDRef}
                                screenshotFormat='image/jpeg'
                                screenshotQuality={1}
                                videoConstraints={{ facingMode: { ideal: 'environment' }, width: { ideal: 3840, min: 1280 }, height: { ideal: 2160, min: 720 } }}
                                className="pm-webcam-preview"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: `scale(${idZoom})`, transformOrigin: 'center' }}
                            />

                        </>
                    ) : (
                        <div className={`pm-cam-placeholder pm-cam-placeholder--${isLandscape ? 'landscape' : 'portrait'}`} />
                    )}
                    {/* Dashed rectangular ID mask */}
                    <svg
                        className="pm-cam-overlay-svg"
                        viewBox={isLandscape ? "0 0 133 100" : "0 0 100 133"}
                        preserveAspectRatio="xMidYMid meet"
                    >
                        {isLandscape ? (
                            <>
                                <defs>
                                    <mask id="id-l-m">
                                        <rect width="133" height="100" fill="white" />
                                        <rect x="13" y="15" width="107" height="70" rx="4" fill="black" />
                                    </mask>
                                </defs>
                                <rect width="133" height="100" fill="rgba(0,0,0,0.48)" mask="url(#id-l-m)" />
                                <rect x="13" y="15" width="107" height="70" rx="4" fill="none" stroke="rgba(255,255,255,0.88)" strokeWidth="0.8" strokeDasharray="4 2.5" />
                                {!useCamPhotoID && (
                                    <g opacity="0.28">
                                        <rect x="17" y="23" width="20" height="26" rx="2" fill="#aaa" />
                                        <rect x="43" y="27" width="52" height="5" rx="2" fill="#aaa" />
                                        <rect x="43" y="37" width="40" height="4" rx="2" fill="#aaa" />
                                        <rect x="43" y="46" width="44" height="4" rx="2" fill="#aaa" />
                                        <rect x="17" y="58" width="24" height="4" rx="2" fill="#aaa" />
                                        <rect x="50" y="58" width="24" height="4" rx="2" fill="#aaa" />
                                    </g>
                                )}
                            </>
                        ) : (
                            <>
                                <defs>
                                    <mask id="id-p-m">
                                        <rect width="100" height="133" fill="white" />
                                        <rect x="12" y="13" width="76" height="107" rx="4" fill="black" />
                                    </mask>
                                </defs>
                                <rect width="100" height="133" fill="rgba(0,0,0,0.48)" mask="url(#id-p-m)" />
                                <rect x="12" y="13" width="76" height="107" rx="4" fill="none" stroke="rgba(255,255,255,0.88)" strokeWidth="0.8" strokeDasharray="4 2.5" />
                                {!useCamPhotoID && (
                                    <g opacity="0.28">
                                        <rect x="24" y="24" width="52" height="42" rx="4" fill="#aaa" />
                                        <rect x="22" y="75" width="56" height="6" rx="2" fill="#aaa" />
                                        <rect x="22" y="86" width="44" height="5" rx="2" fill="#aaa" />
                                        <rect x="22" y="96" width="48" height="5" rx="2" fill="#aaa" />
                                    </g>
                                )}
                            </>
                        )}
                    </svg>

                    <span className="pm-cam-align-hint">{t('PhotoManagement.idCamera.alignHint')}</span>
                </div>

                {/* Bottom capture bar */}
                <div className="pm-cam-bottom-bar">
                    <button className="pm-cam-back-btn" onClick={() => { setCamPhotoID(false); setCurrentStep(4); }}>←</button>
                    <div className="pm-cam-capture-wrap">
                        <span className="pm-cam-capture-label">{t('PhotoManagement.idCamera.cameraLabel')}</span>
                        <button className="pm-cam-shutter" onClick={useCamPhotoID ? mobileCaptureIDFromWebcam : startSelfieCameraPhotoID} disabled={isIDCapturing} style={{ opacity: isIDCapturing ? 0.5 : 1 }}>
                            <div className={`pm-cam-shutter-inner${useCamPhotoID ? '' : ' pm-cam-shutter-inner--start'}`} />
                        </button>
                    </div>
                    <div className="pm-cam-spacer" />
                </div>
            </>
        );

        if (isFullScreen) {
            // Portal to <body> — see selfie camera note above.
            return createPortal(<div className="pm-fullscreen-camera">{cameraContent}</div>, document.body);
        }
        return <div>{cameraContent}</div>;
    };

    // ─── Mobile: sticky header ──────────────────────────────────────────────
    const renderMobileHeader = () => {
        if (useCam || useCamPhotoID) return null;
        return (
            <>
                {/* Sticky header bar */}
                <div className="pm-mobile-header">
                    <div className="pm-mobile-header-icon"><img src={lockIcon} alt="lock" style={{ width: 24, height: 24, objectFit: 'contain' }} /></div>
                    <div className="pm-mobile-header-info">
                        <div className="pm-mobile-header-title">{t('PhotoManagement.PhotoManagement.IdentityVerification')}</div>
                        <div className="pm-mobile-header-sub">{t('PhotoManagement.header.secureTaglineMobile')}</div>
                    </div>
                    <div className="pm-mobile-header-step">
                        {t('PhotoManagement.header.stepOfMobile', { step: currentStep })}
                    </div>
                </div>

                {/* Progress bar + dot indicator */}
                <div className="pm-progress-section">
                    {/* 7-segment bar */}
                    <div className="pm-progress-bar">
                        {[1, 2, 3, 4, 5, 6, 7].map(s => (
                            <div key={s} className={`pm-progress-segment ${s <= currentStep ? 'active' : ''}`} />
                        ))}
                    </div>
                    {/* Label + dots */}
                    <div className="pm-progress-info">
                        <span className="pm-progress-label">
                            {mobileStepLabel}
                        </span>
                        <div className="pm-progress-dots">
                            {[1, 2, 3, 4, 5, 6, 7].map(s => (
                                <div key={s} className={`pm-progress-dot ${s === currentStep ? 'active' : ''}`} />
                            ))}
                        </div>
                    </div>
                </div>
            </>
        );
    };

    // ─── Mobile: step content ───────────────────────────────────────────────
    const renderMobileStep = () => {

        // ── STEP 1: Overview ─────────────────────────────────────────────────
        if (currentStep === 1) return (
            <div className="pm-overview pm-overview--mobile">
                <h1 className="pm-overview__title pm-overview__title--mobile">
                    {t('PhotoManagement.PhotoManagement.OverviewTitle')}
                </h1>
                <p className="pm-overview__desc pm-overview__desc--mobile">
                    {t('PhotoManagement.PhotoManagement.OverviewDesc')}
                </p>

                <div className="pm-overview__items">
                    {[
                        {
                            icon: '🤳',
                            titleKey: 'PhotoManagement.PhotoManagement.SelfiePhotoTitle',
                            subKey: 'PhotoManagement.PhotoManagement.SelfiePhotoSub',
                        },
                        {
                            icon: (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={THEME} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="5" width="20" height="14" rx="2" /><circle cx="8" cy="12" r="2" /><path d="M14 9h4M14 12h4M14 15h2" />
                                </svg>
                            ),
                            titleKey: 'PhotoManagement.PhotoManagement.PhotoIDTitle',
                            subKey: 'PhotoManagement.PhotoManagement.PhotoIDSub',
                        },
                    ].map((item, i) => (
                        <div key={i} className="pm-overview__item">
                            <div className="pm-overview__item-icon">{item.icon}</div>
                            <div>
                                <div className="pm-overview__item-title">{t(item.titleKey)}</div>
                                <div className="pm-overview__item-sub">{t(item.subKey)}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="pm-overview__notice">
                    <span className="pm-overview__notice-icon"><img src={lockIcon} alt="lock" style={{ width: 20, height: 20, objectFit: 'contain', verticalAlign: 'middle' }} /></span>
                    <p className="pm-overview__notice-text">
                        {t('PhotoManagement.PhotoManagement.PrivacyNote')}{' '}
                        <strong className="pm-overview__notice-deleted">
                            {t('PhotoManagement.PhotoManagement.PermanentlyDeleted')}
                        </strong>{' '}
                        {t('PhotoManagement.PhotoManagement.OnceVerified')}
                    </p>
                </div>

                <button
                    onClick={() => setCurrentStep(2)}
                    className="pm-overview__btn"
                    style={{ background: THEME, boxShadow: `0 4px 16px ${THEME_SHADOW}` }}
                >
                    {t('PhotoManagement.PhotoManagement.ContinueBtn')}
                </button>
            </div>
        );

        // ── STEP 2: Selfie Guidelines ─────────────────────────────────────────
        if (currentStep === 2) return (
            <div className="pm-step-container">
                <h2 className="sg__title sg__title--mobile">{t('PhotoManagement.selfieGuidelines.title')}</h2>
                <p className="sg__subtitle sg__subtitle--mobile">{t('PhotoManagement.selfieGuidelines.subtitle')}</p>

                {/* Acceptable + DO bullets */}
                <div className="sg__acceptable-card sg__acceptable-card--mobile">
                    <div className="sg__acceptable-body sg__acceptable-body--mobile">
                        <div className="sg__photo-col">
                            <div className="sg__acceptable-label sg__acceptable-label--mobile">{t('PhotoManagement.selfieGuidelines.acceptable')}</div>
                            <div className="sg__photo-wrap">
                                <img src={logo} alt="Acceptable selfie" className="sg__photo sg__photo--mobile" />
                                <div className="sg__check-badge sg__check-badge--mobile">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div className="sg__do-section">
                            <div className="sg__do-header sg__do-header--mobile">
                                <div className="sg__do-badge sg__do-badge--mobile"><span>✓</span></div>
                                <span className="sg__do-title sg__do-title--mobile">{t('PhotoManagement.selfieGuidelines.doLabel')}</span>
                            </div>
                            {[
                                { icon: '😊', label: t('PhotoManagement.selfieGuidelines.do.faceVisible'), tooltip: t('PhotoManagement.selfieGuidelines.do.faceVisibleTip') },
                                { icon: '💡', label: t('PhotoManagement.selfieGuidelines.do.goodLighting'), tooltip: t('PhotoManagement.selfieGuidelines.do.goodLightingTip') },
                                { icon: '👤', label: t('PhotoManagement.selfieGuidelines.do.headShoulders'), tooltip: t('PhotoManagement.selfieGuidelines.do.headShouldersTip') },
                                { icon: '⬜', label: t('PhotoManagement.selfieGuidelines.do.plainBackground'), tooltip: t('PhotoManagement.selfieGuidelines.do.plainBackgroundTip') },
                            ].map((item, i) => (
                                <div key={i} className="sg__do-item sg__do-item--mobile" data-tooltip={item.tooltip} style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                                    <span className="sg__do-item-icon sg__do-item-icon--mobile">{item.icon}</span>
                                    <span className="sg__do-item-label sg__do-item-label--mobile">{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* DON'T section */}
                <div className="sg__dont-section">
                    <SectionBadge type="dont" />
                    <DontGrid items={[
                        { label: t('PhotoManagement.selfieGuidelines.dont.notCentered'), img: selfiePoorlyFramed, tip: t('PhotoManagement.selfieGuidelines.dont.notCenteredTip') },
                        { label: t('PhotoManagement.selfieGuidelines.dont.noHat'), img: selfieHatSunglasses, tip: t('PhotoManagement.selfieGuidelines.dont.noHatTip') },
                        { label: t('PhotoManagement.selfieGuidelines.dont.notBlurry'), img: selfieBlurry, tip: t('PhotoManagement.selfieGuidelines.dont.notBlurryTip') },
                        { label: t('PhotoManagement.selfieGuidelines.dont.noBusyBackground'), img: selfieBusyBackground, tip: t('PhotoManagement.selfieGuidelines.dont.noBusyBackgroundTip') },
                    ]} />
                </div>

                {renderNavBar({ onBack: () => setCurrentStep(1), onContinue: () => { startSelfieCamera(); setCurrentStep(3); }, continueLabel: t('PhotoManagement.nav.continueToCamera') })}
            </div>
        );

        // ── STEP 3 + 3A: Take Selfie ──────────────────────────────────────────
        if (currentStep === 3) {
            // 3A: Post-capture review
            if (capturedSelfie) return (
                <div className="pm-step-container">
                    {showSelfieBypassModal && (
                        <BypassConfirmModal
                            errors={selfieError}
                            resolveError={resolveValidationError}
                            onConfirm={(reason) => {
                                setSelfieBypassReason(reason);
                                setSelfieBypassConfirmed(true);
                                setShowSelfieBypassModal(false);
                                if (retakingFrom === 'selfie') { setRetakingFrom(null); setCurrentStep(6); }
                                else setCurrentStep(4);
                            }}
                            onCancel={handleSelfieBypassCancel}
                        />
                    )}
                    <h2 className="pm-step-title">{t('PhotoManagement.selfieCamera.title')}</h2>
                    <div className="pm-preview-box" style={{ position: 'relative' }}>
                        {PhotoBlob && <img src={URL.createObjectURL(PhotoBlob)} alt="Captured selfie" className="pm-preview-img" />}
                        <span className={`pm-preview-badge${selfieError.length > 0 && !selfieValidating ? ' pm-preview-badge--error' : ''}`}>
                            {selfieError.length > 0 && !selfieValidating
                                ? t('PhotoManagement.selfieCamera.capturedWithErrors')
                                : t('PhotoManagement.selfieCamera.captured')}
                        </span>
                        <span className="pm-preview-hint">{t('PhotoManagement.selfieCamera.capturedHint')}</span>

                        {selfieValidating && (
                            <div style={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'rgba(0,0,0,0.35)', borderRadius: 'inherit',
                            }}>
                                <div style={{
                                    background: 'rgba(255,255,255,0.95)', borderRadius: '50%',
                                    width: 48, height: 48,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
                                }}>
                                    <Loader scale={0.55} />
                                </div>
                            </div>
                        )}
                    </div>

                    {selfieError.length > 0 && !selfieValidating && (
                        <div className="cco_form_message cco_form_message_error pm-selfie-review__error" style={{ marginBottom: 12 }}>
                            <span className="pm-beta-tag">BETA</span>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {selfieError.map((code, i) => (
                                    <li key={i}>
                                        {resolveValidationError(code)}
                                    </li>
                                ))}
                            </ul>
                            <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                        </div>
                    )}

                    <button onClick={resetImage} className="pm-btn-secondary--bronze" style={{ width: '100%', marginBottom: 4 }}>
                        {t('PhotoManagement.selfieCamera.retakeSelfie')}
                    </button>

                    {renderNavBar({
                        onBack: resetImage,
                        onContinue: handleSelfieReviewContinue,
                        continueLabel: t('PhotoManagement.nav.continue'),
                        backLabel: t('PhotoManagement.nav.back'),
                        continueDisabled: selfieValidating,
                    })}
                </div>
            );

            // 3: Live camera — always open camera immediately on mobile
            return renderSelfieCamera();
        }

        // ── STEP 4: Photo ID Guidelines ───────────────────────────────────────
        if (currentStep === 4) return (
            <div className="sg pm-step-container">
                <h2 className="sg__title sg__title--mobile">{t('PhotoManagement.idGuidelines.title')}</h2>
                <p className="sg__subtitle sg__subtitle--mobile">{t('PhotoManagement.idGuidelines.subtitle')}</p>

                {/* Priority 3A: physical card warning */}


                {/* Acceptable card: ID image + DO checklist side by side */}
                <div className="sg__acceptable-card sg__acceptable-card--mobile" style={{ marginBottom: 16 }}>
                    <div className="sg__acceptable-label sg__acceptable-label--mobile">{t('PhotoManagement.idGuidelines.acceptable')}</div>
                    <div className="sg__acceptable-body sg__acceptable-body--mobile">
                        <div className="sg__photo-col">
                            <div className="sg__photo-wrap">
                                <img
                                    src={idAcceptable}
                                    alt="Acceptable ID"
                                    className="sg__photo sg__photo--mobile"
                                    style={{ width: 120, height: 'auto', borderRadius: 8 }}
                                />
                                <div className="sg__check-badge sg__check-badge--mobile">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                </div>
                            </div>
                            <p style={{ margin: '10px 0 0', fontSize: 10, fontWeight: 700, color: '#1d4ed8', textAlign: 'center' }}>{t('PhotoManagement.idGuidelines.fullCardVisible')}</p>
                        </div>
                        <div className="sg__do-section">
                            <div className="sg__do-header sg__do-header--mobile">
                                <div className="sg__do-badge sg__do-badge--mobile"><span>✓</span></div>
                                <span className="sg__do-title sg__do-title--mobile">{t('PhotoManagement.idGuidelines.doLabel')}</span>
                            </div>
                            {[
                                { icon: '🪪', label: t('PhotoManagement.idGuidelines.do.fullIdVisible'), tooltip: t('PhotoManagement.idGuidelines.do.fullIdVisibleTip') },
                                { icon: '📅', label: t('PhotoManagement.idGuidelines.do.expiryDateClear'), tooltip: t('PhotoManagement.idGuidelines.do.expiryDateClearTip') },
                                { icon: '🎂', label: t('PhotoManagement.idGuidelines.do.dateOfBirthVisible'), tooltip: t('PhotoManagement.idGuidelines.do.dateOfBirthVisibleTip') },
                                { icon: '💡', label: t('PhotoManagement.idGuidelines.do.wellLit'), tooltip: t('PhotoManagement.idGuidelines.do.wellLitTip') },
                            ].map((item, i) => (
                                <div key={i} className="sg__do-item" data-tooltip={item.tooltip} style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                                    <span className="sg__do-item-icon">{item.icon}</span>
                                    <span className="sg__do-item-label">{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Required fields */}
                <div className="pm-required-fields">
                    <span className="pm-required-fields-icon">📋</span>
                    <p className="pm-required-fields-text">
                        <strong>{t('PhotoManagement.idGuidelines.requiredFieldsLabel')}</strong> {t('PhotoManagement.idGuidelines.requiredFields')}
                    </p>
                </div>

                <div style={{ marginBottom: 22 }}>
                    <SectionBadge type="dont" />
                    <DontGrid items={[
                        { label: t('PhotoManagement.idGuidelines.dont.notCropped'), img: idImproperiyCropped, tip: t('PhotoManagement.idGuidelines.dont.notCroppedTip') },
                        { label: t('PhotoManagement.idGuidelines.dont.notBlurry'), img: idBlurry, tip: t('PhotoManagement.idGuidelines.dont.notBlurryTip') },
                        { label: t('PhotoManagement.idGuidelines.dont.dontCoverText'), img: idBlackedOut, tip: t('PhotoManagement.idGuidelines.dont.dontCoverTextTip') },
                        { label: t('PhotoManagement.idGuidelines.dont.mustBeOfficial'), img: idNotOfficial, tip: t('PhotoManagement.idGuidelines.dont.mustBeOfficialTip') },
                    ]} />
                </div>

                {renderNavBar({ onBack: () => setCurrentStep(3), onContinue: () => { startSelfieCameraPhotoID(); setCurrentStep(5); }, continueLabel: t('PhotoManagement.nav.continueToCamera') })}
            </div>
        );

        // ── STEP 5 + 5A: Take ID Photo ────────────────────────────────────────
        if (currentStep === 5) {
            // 5A: Post-capture review
            if (capturedID) return (
                <div className="pm-step-container">
                    {showPhotoIDBypassModal && (
                        <BypassConfirmModal
                            errors={photoIDError}
                            resolveError={resolveValidationError}
                            onConfirm={(reason) => {
                                setPhotoIDBypassReason(reason);
                                setPhotoIDBypassConfirmed(true);
                                setShowPhotoIDBypassModal(false);
                                setRetakingFrom(null);
                                setCurrentStep(6);
                            }}
                            onCancel={handlePhotoIDBypassCancel}
                        />
                    )}
                    <h2 className="pm-step-title">{t('PhotoManagement.idCamera.title')}</h2>
                    <p className="pm-step-note">
                        {t('PhotoManagement.PhotoIDUpload.Note')} <b style={{ color: '#d9534f' }}>{t('PhotoManagement.PhotoIDUpload.permanentlydeleted')}</b> {t('PhotoManagement.PhotoIDUpload.Verified')}
                    </p>
                    <div className="pm-preview-box">
                        {PhotoIDBlob && <img src={URL.createObjectURL(PhotoIDBlob)} alt="Captured ID" className="pm-preview-img" />}
                        <span className={`pm-preview-badge${photoIDError.length > 0 && !photoIDValidating ? ' pm-preview-badge--error' : ''}`}>
                            {photoIDError.length > 0 && !photoIDValidating
                                ? t('PhotoManagement.idCamera.capturedWithErrors')
                                : t('PhotoManagement.selfieCamera.captured')}
                        </span>
                        <span className="pm-preview-hint">{t('PhotoManagement.idCamera.capturedHint')}</span>
                        {photoIDValidating && (
                            <div className="pm-selfie-review__loader-overlay">
                                <div className="pm-selfie-review__loader-spinner">
                                    <Loader scale={0.55} />
                                </div>
                            </div>
                        )}
                    </div>

                    {photoIDError.length > 0 && !photoIDValidating && (
                        <div className="cco_form_message cco_form_message_error pm-selfie-review__error" style={{ marginBottom: 12 }}>
                            <span className="pm-beta-tag">BETA</span>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {photoIDError.map((code, i) => <li key={i}>{resolveValidationError(code)}</li>)}
                            </ul>
                            <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                        </div>
                    )}

                    <button onClick={resetImagePhotoID} className="pm-btn-secondary--bronze" style={{ width: '100%', marginBottom: 4 }}>
                        {t('PhotoManagement.idCamera.retakePhoto')}
                    </button>

                    {renderNavBar({
                        onBack: resetImagePhotoID,
                        onContinue: handleIdReviewContinue,
                        continueLabel: t('PhotoManagement.nav.review'),
                        backLabel: t('PhotoManagement.nav.back'),
                        continueDisabled: photoIDValidating,
                    })}
                </div>
            );

            // 5: Live ID camera
            return (
                <div className="pm-step-container pm-step-container--camera">
                    {faceError && (
                        <div className="cco_form_message cco_form_message_error" style={{ marginBottom: 12 }}>
                            {faceError}
                        </div>
                    )}
                    {renderIDCamera()}
                </div>
            );
        }

        // ── STEP 7: Confirmation ──────────────────────────────────────────────
        if (currentStep === 7) return (
            <div className="pm-success pm-step-container">
                <div className="pm-success__icon-wrap">
                    <div className="pm-success__icon-glow">
                        <div className="pm-success__icon-box">
                            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                    </div>
                </div>
                <h2 className="pm-success__title">{t('PhotoManagement.success.title')}</h2>
                <p className="pm-success__desc">
                    {t('PhotoManagement.success.desc')}
                </p>
                <div className="pm-success__tags">
                    {[t('PhotoManagement.success.selfieTag'), t('PhotoManagement.success.idPhotoTag')].map(tag => (
                        <div key={tag} className="pm-success__tag">{tag}</div>
                    ))}
                </div>
                <a href="/dashboard" className="pm-btn-complete">
                    {t('PhotoManagement.success.returnBtn')}
                </a>
            </div>
        );

        // ── STEP 6: Review & Submit ───────────────────────────────────────────
        if (currentStep === 6) {
            return (
                <div className="pm-step-container">
                    <h2 className="pm-step-title">{t('PhotoManagement.review.title')}</h2>
                    <p className="pm-step-subtitle">{t('PhotoManagement.review.subtitle')}</p>

                    {/* Selfie card */}
                    <div className="pm-review-card pm-review-card--mobile">
                        <div className="pm-review-card__header pm-review-card__header--mobile">
                            <div className="pm-review-card__title-mobile">
                                <span>{t('PhotoManagement.review.selfieLabel')}</span>
                            </div>
                            {selfieError.length > 0
                                ? <span className="pm-review-card__badge--error">{t('PhotoManagement.selfieCamera.capturedWithErrors')}</span>
                                : <span className="pm-review-card__badge pm-review-card__badge--mobile">{t('PhotoManagement.review.readyMobile')}</span>
                            }
                        </div>
                        <div className="pm-review-card__photo">
                            {PhotoBlob && <img src={URL.createObjectURL(PhotoBlob)} alt="Selfie preview" />}
                        </div>
                        {selfieError.length > 0 && (
                            <div className="cco_form_message cco_form_message_error pm-selfie-review__error" style={{ marginBottom: 12 }}>
                                <span className="pm-beta-tag">BETA</span>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {selfieError.map((code, i) => (
                                        <li key={i}>{resolveValidationError(code)}</li>
                                    ))}
                                </ul>
                                <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                            </div>
                        )}
                        <div className={`pm-review-card__footer pm-review-card__footer--mobile${selfieError.length > 0 ? ' pm-review-card__footer--flush' : ''}`}>
                            <button className="pm-review-retake-btn" onClick={() => { resetImage(); setRetakingFrom('selfie'); setCurrentStep(3); }}>
                                {t('PhotoManagement.review.retakeSelfie')}
                            </button>
                        </div>
                    </div>

                    {/* Photo ID card */}
                    <div className="pm-review-card pm-review-card--mobile">
                        <div className="pm-review-card__header pm-review-card__header--mobile">
                            <div className="pm-review-card__title-mobile">
                                <span>{t('PhotoManagement.review.idLabel')}</span>
                            </div>
                            {photoIDError.length > 0
                                ? <span className="pm-review-card__badge--error">{t('PhotoManagement.idCamera.capturedWithErrors')}</span>
                                : <span className="pm-review-card__badge pm-review-card__badge--mobile">{t('PhotoManagement.review.readyMobile')}</span>
                            }
                        </div>
                        <div className="pm-review-card__photo">
                            {PhotoIDBlob && <img src={URL.createObjectURL(PhotoIDBlob)} alt="ID preview" />}
                        </div>
                        {photoIDError.length > 0 && (
                            <div className="cco_form_message cco_form_message_error pm-selfie-review__error" style={{ marginBottom: 12 }}>
                                <span className="pm-beta-tag">BETA</span>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {photoIDError.map((code, i) => (
                                        <li key={i}>{resolveValidationError(code)}</li>
                                    ))}
                                </ul>
                                <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                            </div>
                        )}
                        <div className={`pm-review-card__footer pm-review-card__footer--mobile${photoIDError.length > 0 ? ' pm-review-card__footer--flush' : ''}`}>
                            <button className="pm-review-retake-btn" onClick={() => { resetImagePhotoID(); setRetakingFrom('id'); setCurrentStep(5); }}>
                                {t('PhotoManagement.review.retakePhoto')}
                            </button>
                        </div>
                    </div>

                    {/* Face detection error */}
                    {showFaceError && (
                        <div className="cco_form_message cco_form_message_error pm-review-error pm-review-error--mobile">
                            {faceError ? faceError : t('PhotoManagement.PhotoUpload.PhotoRec')}
                            <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                        </div>
                    )}

                    {/* Submit Verification */}
                    <button
                        onClick={submitHandler}
                        disabled={!isPhotoCroped || !isPhotoIDCroped || showLoading}
                        className="pm-btn-primary pm-btn-primary--full"
                    >
                        {showLoading ? t('PhotoManagement.review.submitting') : t('PhotoManagement.review.submitBtn')}
                    </button>
                </div>
            );
        }

        return null;
    };

    // ─── MAIN RENDER ──────────────────────────────────────────────────────────
    return (
        <div id="main-content" style={{ marginTop: headerMarginTop }}>

            {/* ══════════════ MOBILE VIEW ══════════════ */}
            {isMobile && (
                <div style={{ minHeight: '100vh', background: '#f2f3f7', fontFamily: '"DMSans Regular", sans-serif' }}>
                    {photoQuery.isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 20px' }}><Loader /></div>
                    ) : photoQuery.error ? (
                        <div style={{ padding: '30px 20px' }}><p style={{ color: '#dc2626' }}>{photoQuery.error.message}</p></div>
                    ) : !isSubmitted && photoData && (photoData.length > 0 || CandidateAccessControl === AccessControl.Suspended || CandidateAccessControl === AccessControl.Locked) ? (
                        /* ── Already submitted / locked / suspended banner ── */
                        <div className="pm-status-banner-wrap">
                            <div className="pm-status-banner">
                                {photoData.length > 0 && CandidateAccessControl !== AccessControl.Locked && CandidateAccessControl !== AccessControl.Suspended && (
                                    <p className="pm-status-banner__text">{t('PhotoManagement.PhotoManagement.Request')}</p>
                                )}
                                {(CandidateAccessControl === AccessControl.Locked || CandidateAccessControl === AccessControl.Suspended) && (
                                    <p className="pm-status-banner__text">{t('PhotoManagement.PhotoManagement.Record')}</p>
                                )}
                            </div>
                        </div>
                    ) : (isSubmitted || (photoQuery.isFetched && !photoQuery.isError && (!photoData || photoData.length <= 0)
                        && CandidateAccessControl !== AccessControl.Locked && CandidateAccessControl !== AccessControl.Suspended)) ? (
                        <>
                            {renderMobileHeader()}
                            {isProfileIncomplete && (
                                <div style={{ margin: '12px 16px 0', background: '#fff3cd', color: '#856404', padding: '12px 14px', borderRadius: '10px', borderLeft: '4px solid #ffeeba', fontSize: 13 }}>
                                    {t('PhotoManagement.PhotoManagement.incompleteProfile')}
                                </div>
                            )}
                            <div style={{ paddingBottom: 48 }}>
                                {renderMobileStep()}
                            </div>
                            <BlockUI title={t('PhotoManagement.PhotoManagement.Waiting')} blocking={showLoading && currentStep !== 7} />
                        </>
                    ) : null}
                </div>
            )}

            {/* ══════════════ DESKTOP VIEW ══════════════ */}
            {!isMobile && (
                <div className="pm-main-container">
                    <article className="react-container" style={{ maxWidth: 1100, margin: '0 auto' }}>

                        {/* Breadcrumbs */}
                        <div style={{ marginBottom: 30, fontSize: 16, display: 'flex', alignItems: 'center', gap: 10, fontFamily: '"DMSans Bold"' }}>
                            <a href="/dashboard" style={{ color: THEME, textDecoration: 'none', transition: 'opacity 0.2s', fontWeight: 800 }} onMouseEnter={e => e.currentTarget.style.opacity = '0.8'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                                {t('PhotoManagement.PhotoManagement.Dashboard')}
                            </a>
                            <span style={{ color: '#64748b', fontWeight: 500, fontSize: 14 }}>&gt;</span>
                            <span style={{ color: '#1a1f36', fontWeight: 800 }}>
                                {t('PhotoManagement.PhotoManagement.PhotoManagement')}
                            </span>
                        </div>

                        {isProfileIncomplete && (
                            <div style={{ background: '#fff3cd', color: '#856404', padding: '15px 20px', borderRadius: '12px', borderLeft: '5px solid #ffeeba', marginBottom: 25, fontSize: 15 }}>
                                {t('PhotoManagement.PhotoManagement.incompleteProfile')}
                            </div>
                        )}

                        <div className="cco_row" style={{ marginBottom: "20px" }}>
                            <div className="cco_col">
                                {photoQuery.isLoading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Loader /></div>
                                ) : photoQuery.error ? (
                                    <div style={{ color: '#dc2626', padding: 20 }}>{photoQuery.error.message}</div>
                                ) : !isSubmitted && photoData && (photoData?.length > 0 || CandidateAccessControl === AccessControl.Suspended || CandidateAccessControl === AccessControl.Locked) ? (
                                    <div style={{ background: '#d4edda', color: '#155724', padding: '20px 25px', borderRadius: '12px', borderLeft: '5px solid #c3e6cb' }}>
                                        {photoData?.length > 0 && (CandidateAccessControl !== AccessControl.Locked && CandidateAccessControl !== AccessControl.Suspended) && <p style={{ margin: 0, fontWeight: 600 }}>{t('PhotoManagement.PhotoManagement.Request')}</p>}
                                        {(CandidateAccessControl === AccessControl.Locked || CandidateAccessControl === AccessControl.Suspended) && <p style={{ margin: 0, fontWeight: 600 }}>{t('PhotoManagement.PhotoManagement.Record')} </p>}
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {(isSubmitted || (photoQuery.isFetched && !photoQuery.isError && (!photoData || photoData.length <= 0) && CandidateAccessControl !== AccessControl.Locked && CandidateAccessControl !== AccessControl.Suspended)) && (
                            <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                                {renderDesktopHeader()}
                                <div style={{ padding: '0 40px 50px' }}>
                                    {renderDesktopStep()}
                                </div>
                                <BlockUI title={t('PhotoManagement.PhotoManagement.Waiting')} blocking={showLoading && currentStep !== 7} />
                            </div>
                        )}
                    </article>
                </div>
            )}

        </div>
    );
};
